import React, { useState, useEffect } from 'react';
import '../styles/blog.css';
import EnquiryForm from './EnquiryForm';
import { useParams, useNavigate, Link } from 'react-router-dom';

import {
    FaArrowRight,
    FaBars,
    FaTimes,
    FaCalendarAlt,
    FaTag,
    FaChevronLeft,
    FaChevronRight,
    FaSearch,
    FaArrowLeft,
    FaHome,
    FaFacebookF,
    FaInstagram,
    FaTwitter,
    FaLinkedinIn,
    FaBath,
    FaBed,
    FaBuilding,
    FaUsers,
    FaMapMarkerAlt,
    FaPhone,
    FaEnvelope,
    FaShareAlt,
    FaPrint,
    FaBookmark,
    FaWhatsapp
} from 'react-icons/fa';
import { fetchSheet } from "../utils/googleSheets";
import logo from "../images/logo.png";
import white_logo from "../images/white_logo.png";

const getDriveImageUrl = (url) => {
    if (!url) return null;

    // Already non-Drive image (Unsplash, CDN, etc.)
    if (!url.includes("drive.google.com")) return url;

    let fileId = "";

    // /file/d/FILE_ID
    const match1 = url.match(/\/file\/d\/([^/]+)/);

    // ?id=FILE_ID
    const match2 = url.match(/[?&]id=([^&]+)/);

    if (match1) {
        fileId = match1[1];
    } else if (match2) {
        fileId = match2[1];
    }

    if (!fileId) return null;

    // Google Drive thumbnail endpoint (HOTLINK SAFE)
    return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
};

const Blog = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [blogPosts, setBlogPosts] = useState([]);
    const [filteredPosts, setFilteredPosts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [currentPage, setCurrentPage] = useState(1);
    const [showEnquiryForm, setShowEnquiryForm] = useState(false);
    const [loading, setLoading] = useState(true);

    const { id } = useParams();
    const navigate = useNavigate();
    const postsPerPage = 6;

    // Check if we're viewing a single blog post
    const isSinglePost = !!id;
    const [currentPost, setCurrentPost] = useState(null);

    // Fetch blog posts from Google Sheets
    useEffect(() => {
        const loadBlogData = async () => {
            try {
                setLoading(true);
                const blogData = await fetchSheet("BlogPosts");
                const normalizedBlogs = blogData.map((b) => ({
                    ...b,
                    image: getDriveImageUrl(b.image),
                    featured_image: getDriveImageUrl(b.featured_image),
                    date: b.publish_date || new Date().toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                    })
                }));
                setBlogPosts(normalizedBlogs);
                setFilteredPosts(normalizedBlogs);

                // If we're viewing a single post, find it
                if (id) {
                    const post = normalizedBlogs.find(p => p.id === id);
                    if (post) {
                        setCurrentPost(post);
                    } else {
                        // If post not found, redirect to blog list
                        navigate('/blog');
                    }
                }
            } catch (err) {
                console.error("Error loading blog posts:", err);
            } finally {
                setLoading(false);
            }
        };

        loadBlogData();
    }, [id, navigate]);

    const handleEnquireNow = (e) => {
        e.preventDefault();
        setShowEnquiryForm(true);
    };

    // Handle scroll effect
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 100);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Handle mobile menu toggle
    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
        document.body.style.overflow = !mobileMenuOpen ? 'hidden' : 'auto';
    };

    // Close mobile menu when clicking on a link
    const handleNavLinkClick = () => {
        setMobileMenuOpen(false);
        document.body.style.overflow = 'auto';
    };

    // Get unique categories
    const categories = ['All', ...new Set(blogPosts.map(post => post.category).filter(Boolean))];

    // Handle search
    const handleSearch = (e) => {
        const term = e.target.value.toLowerCase();
        setSearchTerm(term);

        let filtered = blogPosts;

        if (term) {
            filtered = filtered.filter(post =>
                post.title.toLowerCase().includes(term) ||
                post.excerpt.toLowerCase().includes(term) ||
                post.section_1_content?.toLowerCase().includes(term) ||
                post.category?.toLowerCase().includes(term)
            );
        }

        if (selectedCategory !== 'All') {
            filtered = filtered.filter(post => post.category === selectedCategory);
        }

        setFilteredPosts(filtered);
        setCurrentPage(1);
    };

    // Handle category filter
    const handleCategoryFilter = (category) => {
        setSelectedCategory(category);

        let filtered = blogPosts;

        if (category !== 'All') {
            filtered = filtered.filter(post => post.category === category);
        }

        if (searchTerm) {
            filtered = filtered.filter(post =>
                post.title.toLowerCase().includes(searchTerm) ||
                post.excerpt.toLowerCase().includes(searchTerm) ||
                post.section_1_content?.toLowerCase().includes(searchTerm)
            );
        }

        setFilteredPosts(filtered);
        setCurrentPage(1);
    };

    // Sort posts by date in descending order
    const sortedPosts = [...filteredPosts].sort((a, b) => {
        return new Date(b.publish_date || b.date) - new Date(a.publish_date || a.date);
    });

    // Pagination logic
    const indexOfLastPost = currentPage * postsPerPage;
    const indexOfFirstPost = indexOfLastPost - postsPerPage;
    const currentPosts = sortedPosts.slice(indexOfFirstPost, indexOfLastPost);
    const totalPages = Math.ceil(sortedPosts.length / postsPerPage);

    // Pagination controls
    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
    const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));

    // Reset all filters
    const resetFilters = () => {
        setSearchTerm('');
        setSelectedCategory('All');
        setFilteredPosts(blogPosts);
        setCurrentPage(1);
    };

    // Handle go back
    const handleGoBack = () => {
        navigate('/blog');
    };

    // Render content sections
    const renderContentSections = () => {
        if (!currentPost) return null;

        const sections = [];

        // Section 1
        if (currentPost.section_1_title || currentPost.section_1_content) {
            sections.push(
                <div key="section1" className="content-section">
                    {currentPost.section_1_title && <h3>{currentPost.section_1_title}</h3>}
                    {currentPost.section_1_content && <p>{currentPost.section_1_content}</p>}
                </div>
            );
        }

        // Section 2
        if (currentPost.section_2_title || currentPost.section_2_content) {
            sections.push(
                <div key="section2" className="content-section">
                    {currentPost.section_2_title && <h3>{currentPost.section_2_title}</h3>}
                    {currentPost.section_2_content && <p>{currentPost.section_2_content}</p>}
                </div>
            );
        }

        // Section 3
        if (currentPost.section_3_title || currentPost.section_3_content) {
            sections.push(
                <div key="section3" className="content-section">
                    {currentPost.section_3_title && <h3>{currentPost.section_3_title}</h3>}
                    {currentPost.section_3_content && <p>{currentPost.section_3_content}</p>}
                </div>
            );
        }

        // Section 4
        if (currentPost.section_4_title || currentPost.section_4_content) {
            sections.push(
                <div key="section4" className="content-section">
                    {currentPost.section_4_title && <h3>{currentPost.section_4_title}</h3>}
                    {currentPost.section_4_content && <p>{currentPost.section_4_content}</p>}
                </div>
            );
        }

        // Section 5
        if (currentPost.section_5_title || currentPost.section_5_content) {
            sections.push(
                <div key="section5" className="content-section">
                    {currentPost.section_5_title && <h3>{currentPost.section_5_title}</h3>}
                    {currentPost.section_5_content && <p>{currentPost.section_5_content}</p>}
                </div>
            );
        }

        return sections;
    };

    // Share blog post
    const handleShare = () => {
        if (navigator.share) {
            navigator.share({
                title: currentPost?.title,
                text: currentPost?.excerpt,
                url: window.location.href,
            });
        } else {
            // Fallback for browsers that don't support Web Share API
            navigator.clipboard.writeText(window.location.href);
            alert('Link copied to clipboard!');
        }
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading...</p>
            </div>
        );
    }

    return (
        <>
            <EnquiryForm
                isOpen={showEnquiryForm}
                onClose={() => setShowEnquiryForm(false)}
            />
            <div className="blog-page">
                {/* Header */}
                <header className={`main-header ${scrolled ? 'scrolled' : ''}`}>
                    <div className="container header-container">
                        <div className="logo-container">
                            <img src={logo} alt="Logo" style={{ width: 50, height: 50 }} />
                            <div className="logo-text">
                                <h1>Shree Krishna Tiles</h1>
                            </div>
                        </div>

                        <button
                            className="mobile-menu-btn"
                            onClick={toggleMobileMenu}
                            aria-label="Toggle menu"
                        >
                            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
                        </button>

                        <nav className={`main-nav ${mobileMenuOpen ? 'active' : ''}`}>
                            <ul>
                                <li><a href="/" onClick={handleNavLinkClick}>Home</a></li>
                                <li><a href="/#categories" onClick={handleNavLinkClick}>Categories</a></li>
                                <li><a href="/products" onClick={handleNavLinkClick}>Products</a></li>
                                <li><a href="/#about" onClick={handleNavLinkClick}>About Us</a></li>
                                <li><a href="/#testimonials" onClick={handleNavLinkClick}>Testimonials</a></li>
                                <li><a href="/blog" onClick={handleNavLinkClick}>Blog</a></li>
                                <li><a href="/contact" onClick={handleNavLinkClick}>Contact</a></li>
                            </ul>
                        </nav>
                    </div>
                </header>

                {/* Mobile Menu Overlay */}
                {mobileMenuOpen && (
                    <div
                        className="mobile-menu-overlay"
                        onClick={toggleMobileMenu}
                    />
                )}

                {/* Enquire Now Button - Fixed Position */}
                <button className="enquire-now-btn" onClick={handleEnquireNow}>
                    <span>Enquire Now</span>
                    <FaArrowRight className="arrow-icon" />
                </button>

                {isSinglePost ? (
                    // Single Blog Post View
                    currentPost && (
                        <div className="single-post-view">
                            {/* Blog Post Hero */}
                            <section className="post-hero">
                                <div className="container">
                                    <button className="back-to-blog-btn" onClick={handleGoBack}>
                                        <FaArrowLeft /> Back to Blog
                                    </button>

                                    <div className="post-hero-content">
                                        <div className="post-meta">
                                            <span className="post-date">
                                                <FaCalendarAlt /> {currentPost.date}
                                            </span>
                                            <span className="post-category">
                                                <FaTag /> {currentPost.category}
                                            </span>
                                            <span className="post-author">
                                                By {currentPost.author || 'Admin'}
                                            </span>
                                        </div>

                                        <h1 className="post-title">{currentPost.title}</h1>
                                        <p className="post-excerpt">{currentPost.excerpt}</p>

                                    </div>
                                </div>
                            </section>

                            {/* Featured Image */}
                            {currentPost.featured_image && (
                                <section className="post-featured-image">
                                    <div className="container">
                                        <img
                                            src={currentPost.featured_image}
                                            alt={currentPost.title}
                                            className="featured-img"
                                        />
                                    </div>
                                </section>
                            )}

                            {/* Blog Content */}
                            <section className="post-content-section">
                                <div className="container">
                                    <div className="post-content">
                                        {renderContentSections()}
                                    </div>
                                </div>
                            </section>

                            {/* Related Posts */}
                            <section className="related-posts">
                                <div className="container">
                                    <h1>You Might Also Like</h1>
                                    <div className="related-grid">
                                        {(() => {
                                            // Get related posts from the same category (excluding current post)
                                            const relatedPosts = blogPosts.filter(
                                                post => post.id !== currentPost.id && post.category === currentPost.category
                                            );

                                            // Get random posts from other categories if needed
                                            const randomPosts = [];
                                            if (relatedPosts.length < 3) {
                                                const otherPosts = blogPosts.filter(
                                                    post => post.id !== currentPost.id &&
                                                        !relatedPosts.some(rp => rp.id === post.id)
                                                );
                                                // Shuffle and take remaining needed
                                                const needed = 3 - relatedPosts.length;
                                                for (let i = 0; i < needed && i < otherPosts.length; i++) {
                                                    const randomIndex = Math.floor(Math.random() * otherPosts.length);
                                                    randomPosts.push(otherPosts[randomIndex]);
                                                    otherPosts.splice(randomIndex, 1);
                                                }
                                            }

                                            // Combine and limit to 3 posts
                                            const finalPosts = [...relatedPosts, ...randomPosts].slice(0, 3);

                                            return finalPosts.map(post => (
                                                <RelatedPostCard key={post.id} post={post} />
                                            ));
                                        })()}
                                    </div>
                                </div>
                            </section>
                        </div>
                    )
                ) : (
                    // Blog List View
                    <>
                        {/* Blog Hero Section */}
                        <section className="blog-hero">
                            <div className="container">
                                <div className="blog-hero-content">
                                    <h1>Our Blog</h1>
                                    <p>Tips, trends, and inspiration for your tile projects</p>
                                </div>
                            </div>
                        </section>

                        {/* Blog Content */}
                        <section className="blog-content-section">
                            <div className="container">
                                {/* Search and Filters */}
                                <div className="blog-filters">
                                    <div className="search-box">
                                        <FaSearch className="search-icon" />
                                        <input
                                            type="text"
                                            placeholder="Search blog posts..."
                                            value={searchTerm}
                                            onChange={handleSearch}
                                            className="search-input"
                                        />
                                    </div>

                                    <div className="category-filters">
                                        {categories.map(category => (
                                            <button
                                                key={category}
                                                className={`category-filter-btn ${selectedCategory === category ? 'active' : ''}`}
                                                onClick={() => handleCategoryFilter(category)}
                                            >
                                                {category}
                                            </button>
                                        ))}
                                    </div>

                                    {(searchTerm || selectedCategory !== 'All') && (
                                        <button className="reset-filters-btn" onClick={resetFilters}>
                                            Clear Filters
                                        </button>
                                    )}
                                </div>

                                {/* Results Count */}
                                <div className="results-infoo">
                                    <p>Showing {filteredPosts.length} of {blogPosts.length} articles</p>
                                    {searchTerm && (
                                        <p className="search-term">Search results for: "{searchTerm}"</p>
                                    )}
                                </div>

                                {/* Blog Grid */}
                                {currentPosts.length > 0 ? (
                                    <>
                                        <div className="blog-grid">
                                            {currentPosts.map((post) => (
                                                <article key={post.id} className="blog-card">
                                                    <div className="blog-image">
                                                        <img src={post.image || post.featured_image} alt={post.title} />
                                                        {post.category && (
                                                            <div className="blog-category-badge">{post.category}</div>
                                                        )}
                                                    </div>

                                                    <div className="blog-content">
                                                        <div className="blog-meta">
                                                            <span className="blog-date">
                                                                <FaCalendarAlt /> {post.date}
                                                            </span>
                                                            {post.category && (
                                                                <span className="blog-category">
                                                                    <FaTag /> {post.category}
                                                                </span>
                                                            )}
                                                        </div>

                                                        <h3>{post.title}</h3>
                                                        <p className="blog-excerpt">{post.excerpt}</p>

                                                        <a href={`/blog/${post.id}`} className="read-more">
                                                            Read More <FaArrowRight />
                                                        </a>
                                                    </div>
                                                </article>
                                            ))}
                                        </div>

                                        {/* Pagination */}
                                        {totalPages > 1 && (
                                            <div className="pagination">
                                                <button
                                                    className="pagination-btn"
                                                    onClick={prevPage}
                                                    disabled={currentPage === 1}
                                                >
                                                    <FaChevronLeft /> Previous
                                                </button>

                                                <div className="page-numbers">
                                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                                                        <button
                                                            key={number}
                                                            className={`page-number ${currentPage === number ? 'active' : ''}`}
                                                            onClick={() => paginate(number)}
                                                        >
                                                            {number}
                                                        </button>
                                                    ))}
                                                </div>

                                                <button
                                                    className="pagination-btn"
                                                    onClick={nextPage}
                                                    disabled={currentPage === totalPages}
                                                >
                                                    Next <FaChevronRight />
                                                </button>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="no-results">
                                        <h3>No blog posts found</h3>
                                        <p>Try adjusting your search or filters</p>
                                        <button className="btn btn-primary" onClick={resetFilters}>
                                            Reset Filters
                                        </button>
                                    </div>
                                )}
                            </div>
                        </section>
                    </>
                )}

                {/* Footer */}
                <footer id="contact" className="main-footer">
                    <div className="container">
                        <div className="footer-grid">
                            <div className="footer-col">
                                <div className="logo-container">
                                    <img src={white_logo} alt="SKT Logo" width="60" height="60" />
                                    <div>
                                        <h3>Shree Krishna Tiles</h3>
                                        <p>Premium Tiles Since 2001</p>
                                    </div>
                                </div>
                                <p>
                                    Your trusted partner for premium quality tiles.
                                    We provide the finest ceramic, porcelain, vitrified,
                                    and natural stone tiles for residential and commercial spaces.
                                </p>

                                <div className="social-links">
                                    <a href="#" className="social-link"><FaFacebookF /></a>
                                    <a href="#" className="social-link"><FaInstagram /></a>
                                    <a href="#" className="social-link"><FaTwitter /></a>
                                    <a href="#" className="social-link"><FaLinkedinIn /></a>
                                </div>
                            </div>

                            <div className="footer-col">
                                <h3>Quick Links</h3>
                                <ul className="quick-links">
                                    <li><a href="/" onClick={(e) => handleNavLinkClick(e, '/')}><FaHome /> Home</a></li>
                                    <li><a href="/products" onClick={(e) => handleNavLinkClick(e, '/products')}><FaBath /> Products</a></li>
                                    <li><a href="/#categories" onClick={(e) => handleNavLinkClick(e, '/#categories')}><FaBed /> Categories</a></li>
                                    <li><a href="/#about" onClick={(e) => handleNavLinkClick(e, '/#about')}><FaBuilding /> About Us</a></li>
                                    <li><a href="/#testimonials" onClick={(e) => handleNavLinkClick(e, '/#testimonials')}><FaUsers /> Testimonials</a></li>
                                    <li><a href="/blog" onClick={(e) => handleNavLinkClick(e, '/blog')}><FaCalendarAlt /> Blog</a></li>
                                </ul>
                            </div>

                            <div className="footer-col">
                                <h3>Contact Info</h3>
                                <ul className="contact-info">
                                    <li>
                                        <div className="contact-icon">
                                            <FaMapMarkerAlt />
                                        </div>
                                        <div>
                                            <strong>Showroom Address</strong>
                                            <p>4/486, Sungam, Nanjiyampalayam<br />Dharapuram, Tiruppur 638657</p>
                                        </div>
                                    </li>
                                    <li>
                                        <div className="contact-icon">
                                            <FaPhone />
                                        </div>
                                        <div>
                                            <strong>Phone Number</strong>
                                            <p>+91 99421 69120</p>
                                        </div>
                                    </li>
                                    <li>
                                        <div className="contact-icon">
                                            <FaEnvelope />
                                        </div>
                                        <div>
                                            <strong>Email Address</strong>
                                            <p>info@shreekrishnatiles.co.in</p>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="footer-bottom">
                            <p>&copy; {new Date().getFullYear()} Shree Krishna Tiles. All rights reserved.</p>
                            <p>Made with ❤️ by <a href="https://theternos.github.io/portfolio/" target="_blank" rel="noopener noreferrer"> Ternos</a></p>
                        </div>
                    </div>
                </footer>
            </div>
        </>
    );
};

// Component for displaying related post card
const RelatedPostCard = ({ post }) => (
    <article className="related-card">
        <div className="related-image">
            <img src={post.image || post.featured_image} alt={post.title} />
        </div>
        <div className="related-content">
            <h3>{post.title}</h3>
            <p>{post.excerpt?.substring(0, 100) || ''}...</p>
            <Link to={`/blog/${post.id}`} className="read-more">
                Read More <FaArrowRight />
            </Link>
        </div>
    </article>
);

export default Blog;